import React, { useMemo, useEffect, useState } from "react";
import AdminService from "../AdminService";
import { Toast, ToastTitle, Toaster, useToastController } from "@fluentui/react-components";
import { useAdminAuth } from "../AdminAuthContext";
import {
    Button,
    Tooltip,
    makeStyles,
    tokens,
    Spinner,
    CounterBadge
} from "@fluentui/react-components";
import GlobalTableSearch from "./GlobalTableSearch";
import {
    Delete16Regular,
    ArrowUp16Regular,
    ArrowDown16Regular,
    ArrowSort16Regular,
} from "@fluentui/react-icons";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    useReactTable,
    getSortedRowModel
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import { formatDateTime } from "../adminUtil";
import AdminTopbar from "../AdminTopbar";
const useStyles = makeStyles({
    container: {
        padding: "0px 8px",
    },
    header: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "12px",
        fontSize: "18px",
    },
    title: {
        fontSize: "18px",
        fontWeight: 600,
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        background: tokens.colorNeutralBackground1,

        "& th, & td": {
            padding: "6px 6px",
            borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
            textAlign: "left",
        },

        "& thead th": {
            background: tokens.colorNeutralBackground2,
            fontWeight: 600,
        },
    },
    actions: {
        display: "flex",
        gap: "6px",
    },
    topbar: {
        gridColumn: "2",
        gridRow: "1",
        display: "flex",
        alignItems: "center",
        justifyContent: 'space-between',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground1,
        position: "sticky",
        top: '0px'
    },
    globalSearch: {
        marginRight: "24px"
    },
    tooltipWrapper: {
        padding: "8px",
    },
    row: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        marginBottom: "4px",
    },
    activeIcon: {
        color: tokens.colorPaletteGreenForeground1,
    },
    inactiveIcon: {
        color: tokens.colorPaletteRedForeground1,
    },
    total: {
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        paddingBottom: "4px",
        marginBottom: "4px",
    },
    badge: {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "999px",
        fontSize: "12px",
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForegroundLink,
    },
});

const columnHelper = createColumnHelper();

export default function SalesforceOrgsTable({
    tenantId,
    onBack,
}) {
    const { userRoles, userInfo } = useAdminAuth();
    const [orgs, setOrgs] = useState([]);
    const toasterId = "salesforce-org-toaster";
    const { dispatchToast } = useToastController(toasterId);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deletingOrgId, setDeletingOrgId] = useState(null);
    const [sorting, setSorting] = useState([]);
    const [tenantInfo, setTenantInfo] = useState(null);

    const fetchOrgs = async () => {
        if (!tenantId) {
            setOrgs([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await AdminService.getSFOrgsOfTenant(
                tenantId
            );
            setOrgs(res?.data || []);
            setTenantInfo({
                tenantId: res?.tenant?.tenantId || null,
                tenantName: res?.tenant?.tenantName || null,
            });
        } catch (err) {
            setError(err);
            setOrgs([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrgs();
    }, [tenantId, userRoles, userInfo]);

    const handleDelete = async (org) => {
        setDeletingOrgId(org.orgId);
        try {
            await AdminService.deleteSFOrg(
                org.orgId,
                tenantId
            );
            setOrgs((prevOrgs) => prevOrgs.filter((o) => o.orgId !== org.orgId));
        } catch (err) {
            setError(err);
            dispatchToast(
                <Toast><ToastTitle>{err?.message || "Failed to delete org."}</ToastTitle></Toast>,
                { intent: "error" }
            );
        } finally {
            setDeletingOrgId(null);
        }
        dispatchToast(
            <Toast><ToastTitle>{`${org.orgName || org.orgId} was deleted successfully.`}</ToastTitle></Toast>,
            { intent: "success" }
        );
    };
    const styles = useStyles();

    const columns = useMemo(
        () => [
            columnHelper.accessor("orgId", {
                header: "Org ID",
                size: 200,
                cell: (info) => <strong>{info.getValue()}</strong>,
                sortingFn: "alphanumeric",
            }),

            columnHelper.accessor("instanceUrl", {
                header: "Instance URL",
                sortingFn: "alphanumeric",
            }),

            columnHelper.accessor("createdDate", {
                header: "Created At (UTC)",
                cell: (info) => formatDateTime(info.getValue()),
                sortingFn: (rowA, rowB, columnId) => {
                    return new Date(rowA.getValue(columnId) || 0) -
                        new Date(rowB.getValue(columnId) || 0);
                },
            }),

            columnHelper.display({
                id: "actions",
                header: "Actions",
                enableSorting: false, // ✅ important
                cell: ({ row }) => {
                    const org = row.original;
                    return (
                        <div className={styles.actions}>
                            <Tooltip content="Delete Org">
                                <Button
                                    size="small"
                                    appearance="transparent"
                                    icon={<Delete16Regular />}
                                    onClick={() => handleDelete(org)}
                                    disabled={deletingOrgId === org.orgId}
                                />
                            </Tooltip>
                        </div>
                    );
                },
            }),
        ],
        [styles, handleDelete, deletingOrgId]
    );

    // Global filter state
    const [globalFilter, setGlobalFilter] = useState("");

    // Custom global filter function (case-insensitive, string and formatted date support)
    function fuzzyFilter(row, columnId, value, addMeta) {
        let rowValue = row.getValue(columnId);
        if (typeof rowValue === "undefined" || rowValue === null) return false;
        if (columnId === "createdDate") {
            rowValue = formatDateTime(rowValue);
        }
        const itemRank = rankItem(String(rowValue), value, { threshold: 0.1, keepDiacritics: false });
        addMeta(itemRank);
        return itemRank.passed;
    }

    const table = useReactTable({
        data: orgs,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        globalFilterFn: fuzzyFilter,
        state: {
            globalFilter,
            sorting,
        },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        enableMultiSort: true,
    });
    const orgsCount = loading ? null : orgs?.length || 0;

    return (
        <>
            <Toaster toasterId={toasterId} />
            <header className={styles.topbar}>
                <AdminTopbar titleTooltip={tenantInfo?.tenantId} title={tenantInfo?.tenantId ? `Salesforce Orgs - ${tenantInfo?.tenantName ?? tenantInfo?.tenantId}` : "Salesforce Orgs"} onBack={onBack} backTooltip="Back to Customers"
                    titleExtra={
                        <Tooltip content="Total Salesforce Orgs" relationship="label">
                            {loading ? (
                                <Spinner size="tiny" style={{ marginLeft: 4 }} />
                            ) : (
                                <CounterBadge className={styles.badge} count={orgsCount} />
                            )}
                        </Tooltip>
                    }
                />
                <div className={styles.globalSearch}>
                    <GlobalTableSearch
                        value={globalFilter}
                        onChange={setGlobalFilter}
                        placeholder="Search Salesforce orgs..."
                    />
                </div>
            </header>
            <div className={styles.container}>
                {loading ? (
                    <div style={{ padding: "16px" }}>Loading Salesforce orgs...</div>
                ) : error ? (
                    <div style={{ padding: "16px", color: "red" }}>{error?.error ? error?.error : `Error loading Salesforce orgs`}</div>
                ) : (
                    <table className={styles.table}>
                        <thead>
                            {table.getHeaderGroups().map((hg) => (
                                <tr key={hg.id}>
                                    {hg.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            onClick={(e) =>
                                                header.column.getToggleSortingHandler()?.(e)
                                            }
                                            style={{
                                                cursor: header.column.getCanSort() ? "pointer" : "default",
                                            }}
                                        >
                                            <span style={{ display: "inline-flex", alignItems: "center" }}>
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}

                                                {header.column.getCanSort() && (
                                                    <Tooltip
                                                        content={
                                                            header.column.getIsSorted()
                                                                ? `Sorted ${header.column.getIsSorted() === "asc"
                                                                    ? "ascending"
                                                                    : "descending"
                                                                }. Hold Shift to multi-sort.`
                                                                : "Click to sort. Hold Shift to multi-sort."
                                                        }
                                                        relationship="label"
                                                    >
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                marginLeft: 4,
                                                            }}
                                                        >
                                                            {header.column.getIsSorted() === "asc" && (
                                                                <ArrowUp16Regular />
                                                            )}

                                                            {header.column.getIsSorted() === "desc" && (
                                                                <ArrowDown16Regular />
                                                            )}

                                                            {!header.column.getIsSorted() && (
                                                                <ArrowSort16Regular />
                                                            )}

                                                            {/* Multi-sort index */}
                                                            {header.column.getIsSorted() && (
                                                                <span style={{ fontSize: 10, marginLeft: 2 }}>
                                                                    {header.column.getSortIndex() + 1}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>

                        <tbody>
                            {table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map((row) => (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map((cell) => (
                                            <td key={cell.id}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} style={{ padding: "16px" }}>
                                        No Salesforce orgs found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );
}